import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { fmtTime } from "@/lib/youtube";
import QueuePanel from "@/components/music/QueuePanel";
import AddToPlaylist from "@/components/music/AddToPlaylist";
import LyricsPanel from "@/components/music/LyricsPanel";
import { Play, Pause, SkipBack, SkipForward, Volume2, Music2, ListVideo, Mic2, Heart, Shuffle, Repeat, Repeat1, ThumbsUp, ThumbsDown, PictureInPicture2 } from "lucide-react";
import { isLiked, loadLikes, toggleLike } from "@/lib/likes";
import { loadVotes, setVote, voteOf } from "@/lib/votes";

export default function PlayerBar() {
  const { currentTrack, isPlaying, current, duration, volume, togglePlay, next, prev, seek, changeVolume,
    shuffle, toggleShuffle, repeat, cycleRepeat, videoMode, toggleVideoMode } = useMusicPlayer();
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (!currentTrack) return;
    const sync = () => setLiked(isLiked(currentTrack.id));
    sync();
    loadLikes().then(sync);
    window.addEventListener("likes-changed", sync);
    return () => window.removeEventListener("likes-changed", sync);
  }, [currentTrack]);

  const onToggleLike = async () => {
    if (!currentTrack) return;
    setLiked(await toggleLike(currentTrack.id));
  };

  const [vote, setVoteState] = useState(0);

  useEffect(() => {
    if (!currentTrack) return;
    const sync = () => setVoteState(voteOf(currentTrack.id));
    sync();
    loadVotes().then(sync);
    window.addEventListener("votes-changed", sync);
    return () => window.removeEventListener("votes-changed", sync);
  }, [currentTrack]);

  const onVote = async (value) => {
    if (!currentTrack) return;
    const next = await setVote(currentTrack.id, value);
    setVoteState(next);
    // A thumbs down also drops the like, which the API does server side.
    if (next === -1) setLiked(false);
  };
  const { t } = useI18n();
  const [queueOpen, setQueueOpen] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(false);

  if (!currentTrack) return null;

  const pct = duration ? (current / duration) * 100 : 0;

  const onSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seek(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)));
  };

  return (
    <div
      data-testid="music-player-bar"
      className="fixed bottom-[62px] md:bottom-0 left-0 right-0 z-50 h-[76px] md:h-[88px] bg-[#0d0d10] border-t border-white/10 flex items-center px-4 gap-4"
    >
      {/* track info */}
      <div className="flex items-center gap-3 w-[30%] min-w-0">
        {currentTrack.thumbnail ? (
          <img src={currentTrack.thumbnail} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-md bg-white/5 flex items-center justify-center shrink-0"><Music2 className="w-5 h-5 text-zinc-500" /></div>
        )}
        <div className="min-w-0 flex-1">
          <p data-testid="bar-track-title" className="text-sm font-medium truncate">{currentTrack.title}</p>
          {currentTrack.artist ? (
            <Link
              to={`/app/artist/${encodeURIComponent(currentTrack.artist)}`}
              data-testid="bar-artist-link"
              className="text-xs text-zinc-400 hover:text-white hover:underline truncate block"
            >
              {currentTrack.artist}
            </Link>
          ) : null}
        </div>
        <button
          data-testid="bar-thumb-up"
          onClick={() => onVote(1)}
          title={t("thumb_up")}
          className={`hidden sm:block p-2 rounded-full shrink-0 transition-colors ${vote === 1 ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white"}`}
        >
          <ThumbsUp className="w-[18px] h-[18px]" fill={vote === 1 ? "currentColor" : "none"} />
        </button>
        <button
          data-testid="bar-thumb-down"
          onClick={() => onVote(-1)}
          title={t("thumb_down")}
          className={`hidden sm:block p-2 rounded-full shrink-0 transition-colors ${vote === -1 ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white"}`}
        >
          <ThumbsDown className="w-[18px] h-[18px]" fill={vote === -1 ? "currentColor" : "none"} />
        </button>
        <button
          data-testid="bar-like"
          onClick={onToggleLike}
          title={t("like")}
          className={`p-2 rounded-full shrink-0 transition-colors ${liked ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white"}`}
        >
          <Heart className="w-5 h-5" fill={liked ? "currentColor" : "none"} />
        </button>
      </div>

      {/* transport + progress */}
      <div className="flex-1 flex flex-col items-center gap-1.5 max-w-2xl mx-auto">
        <div className="flex items-center gap-4 md:gap-5">
          <button
            data-testid="bar-shuffle"
            onClick={toggleShuffle}
            title={t("shuffle")}
            className={`hidden sm:block transition-colors ${shuffle ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white"}`}
          >
            <Shuffle className="w-[18px] h-[18px]" />
          </button>
          <button data-testid="bar-prev" onClick={prev} className="text-zinc-400 hover:text-white transition-colors">
            <SkipBack className="w-5 h-5" fill="currentColor" />
          </button>
          <button
            data-testid="bar-toggle"
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform"
          >
            {isPlaying ? <Pause className="w-5 h-5" fill="black" /> : <Play className="w-5 h-5 ml-0.5" fill="black" />}
          </button>
          {/* Lives in the transport row rather than beside the artwork: the
              track-info block is only 30% of the bar and a phone has no room
              there for another button without squeezing the title out. */}
          <button
            data-testid="bar-visual"
            onClick={toggleVideoMode}
            title={t("visual")}
            aria-pressed={videoMode}
            className={`transition-colors ${videoMode ? "text-[#FF2A4D]" : "text-zinc-400 hover:text-white"}`}
          >
            <PictureInPicture2 className="w-[18px] h-[18px]" />
          </button>
          <button data-testid="bar-next" onClick={next} className="text-zinc-400 hover:text-white transition-colors">
            <SkipForward className="w-5 h-5" fill="currentColor" />
          </button>
          <button
            data-testid="bar-repeat"
            onClick={cycleRepeat}
            title={t("repeat")}
            className={`hidden sm:block transition-colors ${repeat === "off" ? "text-zinc-400 hover:text-white" : "text-[#FF2A4D]"}`}
          >
            {repeat === "one"
              ? <Repeat1 className="w-[18px] h-[18px]" />
              : <Repeat className="w-[18px] h-[18px]" />}
          </button>
        </div>
        <div className="flex items-center gap-2 w-full">
          <span className="text-[11px] text-zinc-500 font-mono w-9 text-right">{fmtTime(current)}</span>
          <div data-testid="bar-progress" onClick={onSeek} className="group flex-1 h-1 bg-white/15 rounded-full cursor-pointer">
            <div className="h-full rounded-full bg-white group-hover:bg-[#FF2A4D] transition-colors" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[11px] text-zinc-500 font-mono w-9">{fmtTime(duration)}</span>
        </div>
      </div>

      {/* volume + queue */}
      <div className="hidden md:flex items-center gap-3 w-[22%] justify-end">
        <AddToPlaylist trackId={currentTrack.id} />
        <button
          data-testid="lyrics-toggle"
          onClick={() => { setLyricsOpen((o) => !o); setQueueOpen(false); }}
          className={`p-2 rounded-md transition-colors ${lyricsOpen ? "text-[#FF2A4D] bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
          title={t("lyrics")}
        >
          <Mic2 className="w-5 h-5" />
        </button>
        <button
          data-testid="queue-toggle"
          onClick={() => { setQueueOpen((o) => !o); setLyricsOpen(false); }}
          className={`p-2 rounded-md transition-colors ${queueOpen ? "text-[#FF2A4D] bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/10"}`}
          title={t("queue")}
        >
          <ListVideo className="w-5 h-5" />
        </button>
        <Volume2 className="w-4 h-4 text-zinc-400" />
        <input
          data-testid="bar-volume"
          type="range" min="0" max="100" value={volume}
          onChange={(e) => changeVolume(parseInt(e.target.value))}
          className="w-24 accent-[#FF2A4D]"
        />
      </div>

      {queueOpen && <QueuePanel onClose={() => setQueueOpen(false)} />}
      {lyricsOpen && <LyricsPanel onClose={() => setLyricsOpen(false)} />}
    </div>
  );
}
